import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Users, FileCheck, Phone, MapPin, Briefcase, Calendar, ArrowLeft } from "lucide-react";
import i18n from "@/i18n";

const serviciosTipo = [
  "Flete",
  "Cargue/Descargue",
  "Mantenimiento",
  "Servicios generales",
  "Otro"
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatCurrency = (value) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol'
  }).format(value);
};

export default function Proveedores() {
  const { t } = useTranslation();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [proveedorDocumentos, setProveedorDocumentos] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");
  
  const [formData, setFormData] = useState({
    nombre: "",
    cedula: "",
    telefono: "",
    ciudad: "",
    tipo_servicio: "",
    observaciones: ""
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const serviceTypeLabel = (tipo) => t(`suppliers.serviceTypes.${tipo}`, tipo);

  const fetchProveedores = async () => {
    try {
      const params = searchTerm ? `?search=${searchTerm}` : "";
      const response = await axios.get(`${API}/proveedores${params}`);
      setProveedores(response.data);
    } catch (error) {
      toast.error(t('suppliers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProveedores();
  }, [searchTerm]);

  const fetchProveedorDocumentos = async (proveedorId) => {
    setLoadingDocumentos(true);
    try {
      const response = await axios.get(`${API}/proveedores/${proveedorId}/documentos`);
      setProveedorDocumentos(response.data);
    } catch (error) {
      toast.error(t('suppliers.loadDocsError'));
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nombre.trim()) errors.nombre = t('common.required');
    if (!formData.cedula.trim()) errors.cedula = t('common.required');
    if (!formData.telefono.trim()) errors.telefono = t('common.required');
    if (!formData.ciudad.trim()) errors.ciudad = t('common.required');
    if (!formData.tipo_servicio) errors.tipo_servicio = t('common.required');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/proveedores`, formData);
      toast.success(t('suppliers.registerSuccess'));
      setShowCreateModal(false);
      setFormData({
        nombre: "",
        cedula: "",
        telefono: "",
        ciudad: "",
        tipo_servicio: "",
        observaciones: ""
      });
      fetchProveedores();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('suppliers.duplicateCedula'));
      } else {
        toast.error(t('suppliers.registerError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewProfile = (proveedor) => {
    setSelectedProveedor(proveedor);
    fetchProveedorDocumentos(proveedor.id);
    setShowProfileModal(true);
  };

  return (
    <div data-testid="proveedores-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow']">{t('suppliers.title')}</h1>
          <p className="text-slate-500 mt-1">{t('suppliers.subtitle')}</p>
        </div>
        <Button data-testid="btn-registrar-proveedor" onClick={() => setShowCreateModal(true)} className="bg-[#0f2a4a] hover:bg-[#1e3a8a] text-white">
          <Plus className="w-4 h-4 mr-2" />{t('suppliers.registerSupplier')}
        </Button>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input data-testid="search-input" placeholder={t('suppliers.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      {/* Provider Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : proveedores.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">{t('suppliers.noSuppliersFound')}</p>
            <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>{t('suppliers.registerFirst')}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {proveedores.map((proveedor) => (
            <Card 
              key={proveedor.id} 
              data-testid={`proveedor-card-${proveedor.cedula}`}
              className="border-slate-200 hover:shadow-lg transition-all duration-200 cursor-pointer"
              onClick={() => handleViewProfile(proveedor)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#0f2a4a] text-white rounded-full flex items-center justify-center text-lg font-bold">
                    {proveedor.nombre.split(' ').map(n => n[0]).slice(0, 2).join('')}
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded bg-slate-100 text-slate-700 text-xs font-medium">
                    <FileCheck className="w-3 h-3 mr-1" />
                    {proveedor.documentos_count} {t('common.docs')}
                  </span>
                </div>
                
                <h3 className="font-semibold text-slate-900 text-lg mb-1">{proveedor.nombre}</h3>
                <p className="text-slate-500 text-sm mb-4">{t('suppliers.cc')} {proveedor.cedula}</p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {proveedor.ciudad}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    {serviceTypeLabel(proveedor.tipo_servicio)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {t('suppliers.since')} {formatDate(proveedor.fecha_registro)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('suppliers.registerTitle')}</DialogTitle>
            <DialogDescription>{t('suppliers.registerDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">{t('suppliers.fullName')}</Label>
              <Input id="nombre" data-testid="input-nombre" value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} className={formErrors.nombre ? "border-red-500" : ""} />
              {formErrors.nombre && <p className="text-red-500 text-xs mt-1">{formErrors.nombre}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cedula">{t('suppliers.cedula')}</Label>
                <Input id="cedula" data-testid="input-cedula" value={formData.cedula} onChange={(e) => setFormData({...formData, cedula: e.target.value})} placeholder={t('suppliers.cedulaPlaceholder')} className={formErrors.cedula ? "border-red-500" : ""} />
                {formErrors.cedula && <p className="text-red-500 text-xs mt-1">{formErrors.cedula}</p>}
              </div>
              <div>
                <Label htmlFor="telefono">{t('suppliers.phone')}</Label>
                <Input id="telefono" data-testid="input-telefono" value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} placeholder={t('suppliers.phonePlaceholder')} className={formErrors.telefono ? "border-red-500" : ""} />
                {formErrors.telefono && <p className="text-red-500 text-xs mt-1">{formErrors.telefono}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ciudad">{t('suppliers.city')}</Label>
                <Input id="ciudad" data-testid="input-ciudad" value={formData.ciudad} onChange={(e) => setFormData({...formData, ciudad: e.target.value})} className={formErrors.ciudad ? "border-red-500" : ""} />
                {formErrors.ciudad && <p className="text-red-500 text-xs mt-1">{formErrors.ciudad}</p>}
              </div>
              <div>
                <Label htmlFor="tipo_servicio">{t('suppliers.serviceType')}</Label>
                <Select value={formData.tipo_servicio} onValueChange={(v) => setFormData({...formData, tipo_servicio: v})}>
                  <SelectTrigger data-testid="select-tipo-servicio" className={formErrors.tipo_servicio ? "border-red-500" : ""}>
                    <SelectValue placeholder={t('suppliers.selectPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {serviciosTipo.map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>{serviceTypeLabel(tipo)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.tipo_servicio && <p className="text-red-500 text-xs mt-1">{formErrors.tipo_servicio}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="observaciones">{t('common.observationsOptional')}</Label>
              <Textarea id="observaciones" data-testid="input-observaciones" value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} rows={3} placeholder={t('suppliers.additionalNotes')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>{t('common.cancel')}</Button>
              <Button type="submit" data-testid="btn-submit-proveedor" disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2">
                {submitting ? t('suppliers.registering') : t('suppliers.registerButton')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Barlow']">{t('suppliers.profileTitle')}</DialogTitle>
            <DialogDescription>{t('suppliers.profileDescription')}</DialogDescription>
          </DialogHeader>
          {selectedProveedor && (
            <div className="space-y-6">
              <div className="flex items-start gap-6 pb-6 border-b">
                <div className="w-20 h-20 bg-[#0f2a4a] text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                  {selectedProveedor.nombre.split(' ').map(n => n[0]).slice(0, 2).join('')}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-slate-900 font-['Barlow']">{selectedProveedor.nombre}</h2>
                  <p className="text-slate-500">{t('suppliers.cc')} {selectedProveedor.cedula}</p>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />{selectedProveedor.telefono}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />{selectedProveedor.ciudad}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Briefcase className="w-4 h-4 text-slate-400" />{serviceTypeLabel(selectedProveedor.tipo_servicio)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4 text-slate-400" />{t('suppliers.registered')} {formatDate(selectedProveedor.fecha_registro)}
                    </div>
                  </div>
                  {selectedProveedor.observaciones && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 uppercase mb-1">{t('common.observations')}</p>
                      <p className="text-sm text-slate-700">{selectedProveedor.observaciones}</p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Barlow']">{t('suppliers.documentHistory')}</h3>
                {loadingDocumentos ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
                  </div>
                ) : proveedorDocumentos.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <FileCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">{t('suppliers.noDocuments')}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold text-slate-600">{t('suppliers.documentNumber')}</TableHead>
                          <TableHead className="font-semibold text-slate-600">{t('common.date')}</TableHead>
                          <TableHead className="font-semibold text-slate-600">{t('suppliers.concept')}</TableHead>
                          <TableHead className="font-semibold text-slate-600 text-right">{t('suppliers.value')}</TableHead>
                          <TableHead className="font-semibold text-slate-600">{t('common.status')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {proveedorDocumentos.map((doc) => (
                          <TableRow key={doc.id} className="hover:bg-slate-50/50">
                            <TableCell className="font-medium text-slate-900">{doc.numero}</TableCell>
                            <TableCell className="text-slate-600">{formatDate(doc.fecha)}</TableCell>
                            <TableCell className="text-slate-600 max-w-[200px] truncate">{doc.concepto}</TableCell>
                            <TableCell className="text-right font-semibold text-slate-900">{formatCurrency(doc.valor_total)}</TableCell>
                            <TableCell>
                              <span className={`inline-flex px-2.5 py-1 rounded text-xs font-semibold ${
                                doc.estado === 'Aceptada' ? 'bg-green-100 text-green-700' :
                                doc.estado === 'Pendiente' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {doc.estado === 'Aceptada' ? t('common.accepted') : doc.estado === 'Pendiente' ? t('common.pending') : t('common.rejected')}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowProfileModal(false)}>{t('common.close')}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
